<template>
  <el-form ref="form" :model="localData" :rules="rules" label-width="160px" size="small">
    <el-divider>超级管理员信息</el-divider>

    <!-- 管理员类型 -->
    <el-form-item label="管理员类型" prop="adminType">
      <el-select v-model="localData.adminType" placeholder="请选择管理员类型" style="width:100%" @change="handleAdminTypeChange">
        <el-option label="法人" value="LEGAL" />
        <el-option label="经办人" value="SUPERIOR" />
      </el-select>
      <div class="form-tip">法人：使用法人信息作为管理员；经办人：使用经办人信息，需上传授权函</div>
    </el-form-item>

    <!-- ===== 基本信息（共用） ===== -->
    <el-form-item label="姓名" prop="adminName">
      <el-input v-model="localData.adminName" placeholder="请输入管理员姓名" />
    </el-form-item>

    <el-form-item label="手机号" prop="adminPhone">
      <el-input v-model="localData.adminPhone" placeholder="请输入手机号" maxlength="11" />
    </el-form-item>

    <el-form-item label="邮箱" prop="adminEmail">
      <el-input v-model="localData.adminEmail" placeholder="请输入邮箱" />
    </el-form-item>

    <el-form-item label="联系手机" prop="contactPhone">
      <el-input v-model="localData.contactPhone" placeholder="请输入联系手机号" maxlength="11" />
    </el-form-item>

    <el-form-item label="联系邮箱" prop="contactEmail">
      <el-input v-model="localData.contactEmail" placeholder="请输入联系邮箱" />
    </el-form-item>

    <!-- ===== 经办人专属：证件信息 ===== -->
    <template v-if="localData.adminType === 'SUPERIOR'">
      <el-divider>经办人证件信息</el-divider>

      <el-form-item label="证件类型" prop="contactIdDocType">
        <el-select v-model="localData.contactIdDocType" placeholder="请选择证件类型" style="width:100%">
          <el-option label="身份证" value="ID_CARD" />
          <el-option label="护照" value="PASSPORT" />
          <el-option label="军官证" value="OFFICER_CERT" />
          <el-option label="回乡证" value="HOME_RETURN_PERMIT" />
          <el-option label="台胞证" value="COMPATRIOTS_CERT" />
          <el-option label="其他" value="OTHERS" />
        </el-select>
      </el-form-item>

      <!-- ★★★ 新增：经办人身份证号 ★★★ -->
      <el-form-item label="身份证号" prop="contactIdNumber">
        <el-input
            v-model="localData.contactIdNumber"
            placeholder="请输入经办人身份证号"
            maxlength="18"
        />
      </el-form-item>

      <el-form-item label="证件正面" prop="idCardFrontImageId">
        <div class="upload-row">
          <el-input v-model="localData.idCardFrontUrl" placeholder="请点击选择图片" readonly />
          <el-button type="primary" @click="openImageSelector('idCardFront')">选择图片</el-button>
          <el-button
              v-if="localData.idCardFrontUrl"
              type="warning"
              size="small"
              @click="refreshMediaId('idCardFront')"
              :loading="refreshing"
          >刷新MediaId</el-button>
        </div>
        <div v-if="localData.idCardFrontUrl" class="image-preview">
          <el-image :src="localData.idCardFrontUrl" class="thumb" fit="cover" />
        </div>
        <div v-if="localData.idCardFrontMediaId" class="media-id-info">
          <span class="media-id-label">微信MediaId:</span>
          <span class="media-id-value">{{ localData.idCardFrontMediaId }}</span>
          <el-button type="text" size="mini" @click="copyMediaId(localData.idCardFrontMediaId)">复制</el-button>
        </div>
        <div v-else-if="localData.idCardFrontUrl" class="media-id-info" style="color:#e6a23c;">
          ⚠️ 正在获取MediaId...
        </div>
      </el-form-item>

      <el-form-item label="证件反面" prop="idCardBackImageId">
        <div class="upload-row">
          <el-input v-model="localData.idCardBackUrl" placeholder="请点击选择图片" readonly />
          <el-button type="primary" @click="openImageSelector('idCardBack')">选择图片</el-button>
          <el-button
              v-if="localData.idCardBackUrl"
              type="warning"
              size="small"
              @click="refreshMediaId('idCardBack')"
              :loading="refreshing"
          >刷新MediaId</el-button>
        </div>
        <div v-if="localData.idCardBackUrl" class="image-preview">
          <el-image :src="localData.idCardBackUrl" class="thumb" fit="cover" />
        </div>
        <div v-if="localData.idCardBackMediaId" class="media-id-info">
          <span class="media-id-label">微信MediaId:</span>
          <span class="media-id-value">{{ localData.idCardBackMediaId }}</span>
          <el-button type="text" size="mini" @click="copyMediaId(localData.idCardBackMediaId)">复制</el-button>
        </div>
        <div v-else-if="localData.idCardBackUrl" class="media-id-info" style="color:#e6a23c;">
          ⚠️ 正在获取MediaId...
        </div>
      </el-form-item>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="有效期开始" prop="idCardPeriodBegin">
            <el-date-picker
                v-model="localData.idCardPeriodBegin"
                type="date"
                placeholder="选择"
                style="width:100%"
                value-format="yyyy-MM-dd"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="有效期结束" prop="idCardPeriodEnd">
            <el-date-picker
                v-model="localData.idCardPeriodEnd"
                type="date"
                placeholder="长期可不选"
                style="width:100%"
                value-format="yyyy-MM-dd"
                clearable
            />
          </el-form-item>
        </el-col>
      </el-row>

      <!-- ===== 授权函 ===== -->
      <el-divider>授权函</el-divider>
      <el-alert
          title="经办人需提交授权函"
          type="info"
          description="授权经办人办理微信支付特约商户进件事宜"
          show-icon
          :closable="false"
          style="margin-bottom:16px"
      />

      <el-form-item label="授权函" prop="authorizationLetterImageId">
        <div class="upload-row">
          <el-input v-model="localData.authorizationLetterUrl" placeholder="请点击选择图片" readonly />
          <el-button type="primary" @click="openImageSelector('authorizationLetter')">选择图片</el-button>
          <el-button
              v-if="localData.authorizationLetterUrl"
              type="warning"
              size="small"
              @click="refreshMediaId('authorizationLetter')"
              :loading="refreshing"
          >刷新MediaId</el-button>
        </div>
        <div v-if="localData.authorizationLetterUrl" class="image-preview">
          <el-image :src="localData.authorizationLetterUrl" class="thumb" fit="cover" />
        </div>
        <div v-if="localData.authorizationLetterMediaId" class="media-id-info">
          <span class="media-id-label">微信MediaId:</span>
          <span class="media-id-value">{{ localData.authorizationLetterMediaId }}</span>
          <el-button type="text" size="mini" @click="copyMediaId(localData.authorizationLetterMediaId)">复制</el-button>
        </div>
      </el-form-item>
    </template>

    <!-- ===== 法人模式提示 ===== -->
    <template v-if="localData.adminType === 'LEGAL'">
      <el-alert
          title="法人模式"
          type="success"
          description="使用法人信息作为超级管理员，无需额外上传授权函"
          show-icon
          :closable="false"
          style="margin-top:16px"
      />
    </template>

    <div class="module-footer">
      <el-button type="primary" @click="submitModule" :loading="saving">保 存</el-button>
    </div>

    <!-- 图片选择器 -->
    <image-selector
        v-model="showImageSelector"
        :max-count="1"
        @select="handleImageSelect"
    />
  </el-form>
</template>

<script>
import api from '@/utils/api'
import ImageSelector from '@/components/ImageSelector'

export default {
  name: 'AdminInfo',
  components: { ImageSelector },
  props: {
    data: { type: Object, default: () => ({}) },
    applyId: { type: Number, default: null },
    applyNo: { type: String, default: '' },
    merchantId: { type: Number, default: null }
  },
  data() {
    return {
      saving: false,
      refreshing: false,
      showImageSelector: false,
      currentUploadField: '',
      localData: {
        adminType: 'LEGAL',
        adminName: '',
        adminPhone: '',
        adminEmail: '',
        contactPhone: '',
        contactEmail: '',
        contactIdDocType: 'ID_CARD',
        contactIdNumber: '',  // ★★★ 新增：经办人身份证号 ★★★
        idCardFrontUrl: '',
        idCardFrontMediaId: '',
        idCardFrontImageId: null,
        idCardBackUrl: '',
        idCardBackMediaId: '',
        idCardBackImageId: null,
        idCardPeriodBegin: '',
        idCardPeriodEnd: '',
        authorizationLetterUrl: '',
        authorizationLetterMediaId: '',
        authorizationLetterImageId: null
      },
      rules: {
        adminType: [{ required: true, message: '请选择管理员类型', trigger: 'change' }],
        adminName: [{ required: true, message: '请输入管理员姓名', trigger: 'blur' }],
        adminPhone: [
          { required: true, message: '请输入手机号', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号', trigger: 'blur' }
        ],
        adminEmail: [
          { required: true, message: '请输入邮箱', trigger: 'blur' },
          { pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, message: '请输入有效的邮箱', trigger: 'blur' }
        ],
        contactPhone: [
          { required: true, message: '请输入联系手机号', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号', trigger: 'blur' }
        ],
        contactEmail: [
          { required: true, message: '请输入联系邮箱', trigger: 'blur' },
          { pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, message: '请输入有效的邮箱', trigger: 'blur' }
        ]
      }
    }
  },
  watch: {
    data: {
      immediate: true,
      async handler(val) {
        console.log('========== [AdminInfo] watch.data 触发 ==========')
        console.log('[AdminInfo] 接收到的 data:', JSON.stringify(val, null, 2))
        console.log('[AdminInfo] 当前 applyId:', this.applyId)

        if (val && Object.keys(val).length > 0) {
          console.log('[AdminInfo] data 有数据，开始加载')

          // ★★★ 核心修复：直接从 val 中读取所有字段，支持多种字段名 ★★★
          this.localData = {
            // 基本信息
            adminType: val.adminType !== undefined ? val.adminType : 'LEGAL',
            adminName: val.adminName || '',
            adminPhone: val.adminPhone || '',
            adminEmail: val.adminEmail || '',
            contactPhone: val.contactPhone || '',
            contactEmail: val.contactEmail || '',
            contactIdDocType: val.contactIdDocType || 'ID_CARD',
            // ★★★ 新增：经办人身份证号 ★★★
            contactIdNumber: val.contactIdNumber || '',

            // ★★★ 经办人正面：优先 contactIdDocCopy，其次 idCardFront ★★★
            idCardFrontMediaId: val.contactIdDocCopyMediaId || val.idCardFrontMediaId || '',
            idCardFrontImageId: val.contactIdDocCopyImageId !== undefined
                ? val.contactIdDocCopyImageId
                : (val.idCardFrontImageId !== undefined ? val.idCardFrontImageId : null),

            // ★★★ 经办人反面：优先 contactIdDocCopyBack，其次 idCardBack ★★★
            idCardBackMediaId: val.contactIdDocCopyBackMediaId || val.idCardBackMediaId || '',
            idCardBackImageId: val.contactIdDocCopyBackImageId !== undefined
                ? val.contactIdDocCopyBackImageId
                : (val.idCardBackImageId !== undefined ? val.idCardBackImageId : null),

            // ★★★ 有效期：优先 contact，其次 idCard ★★★
            idCardPeriodBegin: val.contactPeriodBegin !== undefined
                ? this.timestampToDateStr(val.contactPeriodBegin)
                : (val.idCardPeriodBegin !== undefined ? this.timestampToDateStr(val.idCardPeriodBegin) : ''),
            idCardPeriodEnd: val.contactPeriodEnd !== undefined
                ? this.timestampToDateStr(val.contactPeriodEnd)
                : (val.idCardPeriodEnd !== undefined ? this.timestampToDateStr(val.idCardPeriodEnd) : ''),

            // ★★★ 授权函 ★★★
            authorizationLetterMediaId: val.authorizationLetterMediaId || '',
            authorizationLetterImageId: val.authorizationLetterImageId !== undefined ? val.authorizationLetterImageId : null,

            // ★★★ URL 直接使用 val 中的值 ★★★
            idCardFrontUrl: val.idCardFrontUrl || '',
            idCardBackUrl: val.idCardBackUrl || '',
            authorizationLetterUrl: val.authorizationLetterUrl || ''
          }

          console.log('[AdminInfo] 加载后的 localData:')
          console.log('  contactIdNumber:', this.localData.contactIdNumber)
          console.log('  idCardFrontImageId:', this.localData.idCardFrontImageId)
          console.log('  idCardBackImageId:', this.localData.idCardBackImageId)
          console.log('  authorizationLetterImageId:', this.localData.authorizationLetterImageId)
          console.log('  idCardFrontMediaId:', this.localData.idCardFrontMediaId)
          console.log('  idCardBackMediaId:', this.localData.idCardBackMediaId)

          // 加载图片 URL
          await this.loadImageUrls(val)
        } else {
          console.log('[AdminInfo] data 为空，跳过加载')
        }
      }
    },
    'localData.adminType'(val) {
      console.log('[AdminInfo] adminType 变更:', val)
      const isSuperior = val === 'SUPERIOR'
      this.rules.contactIdDocType = isSuperior
          ? [{ required: true, message: '请选择证件类型', trigger: 'change' }]
          : [{ required: false }]
      // ★★★ 经办人身份证号验证 ★★★
      this.rules.contactIdNumber = isSuperior
          ? [
            { required: true, message: '请输入经办人身份证号', trigger: 'blur' },
            { pattern: /(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/, message: '请输入有效的身份证号', trigger: 'blur' }
          ]
          : [{ required: false }]
      this.rules.idCardFrontImageId = isSuperior
          ? [{ required: true, message: '请上传证件正面', trigger: 'change' }]
          : [{ required: false }]
      this.rules.idCardBackImageId = isSuperior
          ? [{ required: true, message: '请上传证件反面', trigger: 'change' }]
          : [{ required: false }]
      this.rules.idCardPeriodBegin = isSuperior
          ? [{ required: true, message: '请选择有效期开始', trigger: 'change' }]
          : [{ required: false }]
      this.rules.idCardPeriodEnd = isSuperior
          ? [{ required: false }]
          : [{ required: false }]
      this.rules.authorizationLetterImageId = isSuperior
          ? [{ required: true, message: '请上传授权函', trigger: 'change' }]
          : [{ required: false }]

      if (!isSuperior) {
        this.localData.contactIdDocType = 'ID_CARD'
        this.localData.contactIdNumber = ''  // ★★★ 清空经办人身份证号 ★★★
        this.localData.idCardFrontMediaId = ''
        this.localData.idCardFrontImageId = null
        this.localData.idCardFrontUrl = ''
        this.localData.idCardBackMediaId = ''
        this.localData.idCardBackImageId = null
        this.localData.idCardBackUrl = ''
        this.localData.idCardPeriodBegin = ''
        this.localData.idCardPeriodEnd = ''
        this.localData.authorizationLetterMediaId = ''
        this.localData.authorizationLetterImageId = null
        this.localData.authorizationLetterUrl = ''
      }
      this.$nextTick(() => { if (this.$refs.form) this.$refs.form.clearValidate() })
    }
  },
  methods: {
    // ================================================================
    // ========== 根据 imageId 加载图片 URL ==========
    // ================================================================
    async loadImageUrls(data) {
      console.log('========== [AdminInfo] loadImageUrls 开始 ==========')
      console.log('[AdminInfo] data.contactIdDocCopyImageId:', data.contactIdDocCopyImageId)
      console.log('[AdminInfo] data.contactIdDocCopyBackImageId:', data.contactIdDocCopyBackImageId)
      console.log('[AdminInfo] data.authorizationLetterImageId:', data.authorizationLetterImageId)
      console.log('[AdminInfo] data.idCardFrontImageId:', data.idCardFrontImageId)
      console.log('[AdminInfo] data.idCardBackImageId:', data.idCardBackImageId)

      // ★★★ 正面：尝试多个字段名 ★★★
      let frontImageId = data.contactIdDocCopyImageId
      if (frontImageId === undefined || frontImageId === null) {
        frontImageId = data.idCardFrontImageId
      }
      if (frontImageId) {
        console.log('[AdminInfo] ✅ 加载正面图片, id=', frontImageId)
        try {
          const res = await api.image_getImageById({ id: frontImageId })
          if (res?.data?.success && res.data.data?.url) {
            this.localData.idCardFrontUrl = res.data.data.url
            console.log('[AdminInfo] ✅ 加载正面图片成功:', res.data.data.url)
          }
        } catch (e) {
          console.error('[AdminInfo] ❌ 加载正面图片失败:', e)
        }
      } else {
        console.warn('[AdminInfo] ⚠️ 正面 imageId 为空')
      }

      // ★★★ 反面：尝试多个字段名 ★★★
      let backImageId = data.contactIdDocCopyBackImageId
      if (backImageId === undefined || backImageId === null) {
        backImageId = data.idCardBackImageId
      }
      if (backImageId) {
        console.log('[AdminInfo] ✅ 加载反面图片, id=', backImageId)
        try {
          const res = await api.image_getImageById({ id: backImageId })
          if (res?.data?.success && res.data.data?.url) {
            this.localData.idCardBackUrl = res.data.data.url
            console.log('[AdminInfo] ✅ 加载反面图片成功:', res.data.data.url)
          }
        } catch (e) {
          console.error('[AdminInfo] ❌ 加载反面图片失败:', e)
        }
      } else {
        console.warn('[AdminInfo] ⚠️ 反面 imageId 为空')
      }

      // ★★★ 授权函 ★★★
      const authImageId = data.authorizationLetterImageId
      if (authImageId) {
        console.log('[AdminInfo] ✅ 加载授权函, id=', authImageId)
        try {
          const res = await api.image_getImageById({ id: authImageId })
          if (res?.data?.success && res.data.data?.url) {
            this.localData.authorizationLetterUrl = res.data.data.url
            console.log('[AdminInfo] ✅ 加载授权函成功:', res.data.data.url)
          }
        } catch (e) {
          console.error('[AdminInfo] ❌ 加载授权函失败:', e)
        }
      } else {
        console.warn('[AdminInfo] ⚠️ 授权函 imageId 为空')
      }

      console.log('[AdminInfo] loadImageUrls 执行完毕:')
      console.log('  idCardFrontUrl:', this.localData.idCardFrontUrl)
      console.log('  idCardBackUrl:', this.localData.idCardBackUrl)
      console.log('  authorizationLetterUrl:', this.localData.authorizationLetterUrl)
      console.log('========== [AdminInfo] loadImageUrls 结束 ==========')
    },

    // ================================================================
    // ========== 工具方法 ==========
    // ================================================================

    timestampToDateStr(timestamp) {
      if (!timestamp || timestamp === 0) return ''
      const date = new Date(timestamp)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    toTimestamp(dateStr) {
      if (!dateStr || dateStr === '' || dateStr === null) return null
      if (typeof dateStr === 'number') return dateStr
      const parts = dateStr.split('-')
      if (parts.length === 3) {
        return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]), 0, 0, 0, 0).getTime()
      }
      return null
    },

    // ================================================================
    // ========== 图片选择 ==========
    // ================================================================

    openImageSelector(field) {
      console.log('[AdminInfo] openImageSelector, field:', field)
      this.currentUploadField = field
      this.showImageSelector = true
    },

    async handleImageSelect(images) {
      console.log('========== [AdminInfo] handleImageSelect 开始 ==========')
      console.log('[AdminInfo] images:', images)
      console.log('[AdminInfo] currentUploadField:', this.currentUploadField)

      if (!images || images.length === 0) {
        console.warn('[AdminInfo] images 为空，返回')
        return
      }

      const image = images[0]
      console.log('[AdminInfo] image 对象:', {
        id: image.id,
        url: image.url,
        mediaId: image.mediaId,
        file: image.file ? image.file.name : '无文件对象'
      })

      const imageUrl = image.url
      const imageId = image.id

      const urlField = this.getUrlField(this.currentUploadField)
      const idField = this.getIdField(this.currentUploadField)
      const mediaIdField = this.getMediaIdField(this.currentUploadField)

      console.log('[AdminInfo] 字段映射:', { urlField, idField, mediaIdField, imageId })

      if (urlField) {
        this.$set(this.localData, urlField, imageUrl)
        console.log('[AdminInfo] 设置 URL:', urlField, '=', imageUrl)
      }
      if (idField) {
        this.$set(this.localData, idField, imageId)
        console.log('[AdminInfo] 设置 ImageId:', idField, '=', imageId)
      }

      // 如果图片自带 mediaId，直接使用
      if (image.mediaId && mediaIdField) {
        this.$set(this.localData, mediaIdField, image.mediaId)
        console.log('[AdminInfo] 使用已有 MediaId:', image.mediaId)
        console.log('========== [AdminInfo] handleImageSelect 结束 ==========')
        return
      }

      const purpose = this.getPurpose(this.currentUploadField)
      console.log('[AdminInfo] purpose:', purpose)

      if (purpose && imageUrl) {
        console.log('[AdminInfo] 开始调用 fetchMediaId...')
        try {
          const res = await api.subMerchant_fetchMediaId({ imageUrl, purpose })
          console.log('[AdminInfo] fetchMediaId 响应:', res)

          const responseData = res?.data || res
          if (responseData?.success && responseData?.code === 200) {
            const data = responseData?.data || {}
            const mediaId = data?.media_id || data?.mediaId
            console.log('[AdminInfo] 获取到 mediaId:', mediaId)

            if (mediaId && mediaIdField) {
              this.$set(this.localData, mediaIdField, mediaId)
              console.log('[AdminInfo] ✅ 设置 MediaId:', mediaIdField, '=', mediaId)
              this.$forceUpdate()
            } else {
              console.warn('[AdminInfo] ⚠️ mediaId 为空')
            }
          } else {
            console.error('[AdminInfo] ❌ fetchMediaId 失败:', responseData?.message)
          }
        } catch (e) {
          console.error('[AdminInfo] ❌ fetchMediaId 异常:', e)
        }
      }

      // 手动触发验证
      this.$nextTick(() => {
        if (this.$refs.form && idField) {
          this.$refs.form.validateField(idField)
        }
      })

      console.log('[AdminInfo] handleImageSelect 执行完毕:')
      console.log('  idCardFrontImageId:', this.localData.idCardFrontImageId)
      console.log('  idCardBackImageId:', this.localData.idCardBackImageId)
      console.log('  authorizationLetterImageId:', this.localData.authorizationLetterImageId)
      console.log('========== [AdminInfo] handleImageSelect 结束 ==========')
    },

    // ================================================================
    // ========== 手动刷新 MediaId ==========
    // ================================================================

    async refreshMediaId(field) {
      const urlField = this.getUrlField(field)
      const mediaIdField = this.getMediaIdField(field)
      const imageUrl = this.localData[urlField]

      if (!imageUrl) {
        this.$message.warning('请先选择图片')
        return
      }

      this.refreshing = true
      const purpose = this.getPurpose(field)
      try {
        console.log('[AdminInfo] 手动刷新 MediaId:', { imageUrl, purpose })
        const res = await api.subMerchant_fetchMediaId({ imageUrl, purpose })
        const responseData = res?.data || res
        if (responseData?.success && responseData?.code === 200) {
          const data = responseData?.data || {}
          const mediaId = data?.media_id || data?.mediaId
          if (mediaId) {
            this.$set(this.localData, mediaIdField, mediaId)
            this.$forceUpdate()
            this.$message.success('MediaId刷新成功')
            console.log('[AdminInfo] ✅ MediaId刷新成功:', mediaId)
          } else {
            this.$message.warning('未获取到MediaId')
          }
        } else {
          this.$message.error(responseData?.message || '刷新失败')
        }
      } catch (e) {
        console.error('[AdminInfo] 刷新 MediaId 失败:', e)
        this.$message.error('刷新失败')
      } finally {
        this.refreshing = false
      }
    },

    // ================================================================
    // ========== 复制 MediaId ==========
    // ================================================================

    copyMediaId(mediaId) {
      if (!mediaId) return
      if (navigator.clipboard) {
        navigator.clipboard.writeText(mediaId).then(() => {
          this.$message.success('MediaId已复制')
        }).catch(() => {
          this.$message.warning('复制失败，请手动复制')
        })
      } else {
        const input = document.createElement('input')
        input.value = mediaId
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
        this.$message.success('MediaId已复制')
      }
    },

    // ================================================================
    // ========== 字段映射工具 ==========
    // ================================================================

    getUrlField(field) {
      const map = {
        'idCardFront': 'idCardFrontUrl',
        'idCardBack': 'idCardBackUrl',
        'authorizationLetter': 'authorizationLetterUrl'
      }
      return map[field] || ''
    },

    getIdField(field) {
      const map = {
        'idCardFront': 'idCardFrontImageId',
        'idCardBack': 'idCardBackImageId',
        'authorizationLetter': 'authorizationLetterImageId'
      }
      return map[field] || ''
    },

    getMediaIdField(field) {
      const map = {
        'idCardFront': 'idCardFrontMediaId',
        'idCardBack': 'idCardBackMediaId',
        'authorizationLetter': 'authorizationLetterMediaId'
      }
      return map[field] || ''
    },

    getPurpose(field) {
      const map = {
        'idCardFront': 'id_card_copy',
        'idCardBack': 'id_card_national',
        'authorizationLetter': 'business_authorization_letter'
      }
      return map[field] || ''
    },

    // ================================================================
    // ========== 管理员类型切换 ==========
    // ================================================================

    handleAdminTypeChange(val) {
      console.log('[AdminInfo] handleAdminTypeChange:', val)
      if (val === 'LEGAL') {
        this.localData.contactIdDocType = 'ID_CARD'
        this.localData.contactIdNumber = ''  // ★★★ 清空经办人身份证号 ★★★
        this.localData.authorizationLetterMediaId = ''
        this.localData.authorizationLetterImageId = null
        this.localData.authorizationLetterUrl = ''
        this.localData.idCardFrontMediaId = ''
        this.localData.idCardFrontImageId = null
        this.localData.idCardFrontUrl = ''
        this.localData.idCardBackMediaId = ''
        this.localData.idCardBackImageId = null
        this.localData.idCardBackUrl = ''
        this.localData.idCardPeriodBegin = ''
        this.localData.idCardPeriodEnd = ''
      }
    },

    // ================================================================
    // ========== 表单验证 ==========
    // ================================================================

    validate(cb) {
      if (this.$refs.form) this.$refs.form.validate(cb)
      else cb(false)
    },

    // ================================================================
    // ========== 提交保存 ==========
    // ================================================================

    async submitModule() {
      console.log('========== [AdminInfo] submitModule 开始 ==========')
      console.log('[AdminInfo] 当前 applyId:', this.applyId)
      console.log('[AdminInfo] 当前 localData:')
      console.log('  contactIdNumber:', this.localData.contactIdNumber)
      console.log('  idCardFrontImageId:', this.localData.idCardFrontImageId)
      console.log('  idCardBackImageId:', this.localData.idCardBackImageId)
      console.log('  authorizationLetterImageId:', this.localData.authorizationLetterImageId)
      console.log('  idCardFrontMediaId:', this.localData.idCardFrontMediaId)
      console.log('  idCardBackMediaId:', this.localData.idCardBackMediaId)
      console.log('  authorizationLetterMediaId:', this.localData.authorizationLetterMediaId)

      this.$refs.form.validate(async (valid) => {
        if (!valid) {
          console.warn('[AdminInfo] 表单验证失败')
          this.$message.warning('请完善必填信息')
          return
        }
        this.saving = true
        try {
          const params = {
            id: this.applyId || null,
            applyNo: this.applyNo || '',
            merchantId: this.merchantId,
            rejectReason: 'admin',
            adminType: this.localData.adminType,
            adminName: this.localData.adminName,
            adminPhone: this.localData.adminPhone,
            adminEmail: this.localData.adminEmail,
            contactPhone: this.localData.contactPhone,
            contactEmail: this.localData.contactEmail,
            authorizationLetterMediaId: this.localData.authorizationLetterMediaId,
            authorizationLetterImageId: this.localData.authorizationLetterImageId,
            contactIdDocType: this.localData.contactIdDocType || 'ID_CARD',
            // ★★★ 新增：经办人身份证号 ★★★
            contactIdNumber: this.localData.contactIdNumber || '',
            contactIdDocCopyMediaId: this.localData.idCardFrontMediaId,
            contactIdDocCopyImageId: this.localData.idCardFrontImageId,
            contactIdDocCopyBackMediaId: this.localData.idCardBackMediaId,
            contactIdDocCopyBackImageId: this.localData.idCardBackImageId,
            contactPeriodBegin: this.toTimestamp(this.localData.idCardPeriodBegin),
            contactPeriodEnd: this.toTimestamp(this.localData.idCardPeriodEnd)
          }

          console.log('[AdminInfo] 提交参数:')
          console.log('  contactIdNumber:', params.contactIdNumber)
          console.log('  contactIdDocCopyImageId:', params.contactIdDocCopyImageId)
          console.log('  contactIdDocCopyBackImageId:', params.contactIdDocCopyBackImageId)
          console.log('  authorizationLetterImageId:', params.authorizationLetterImageId)

          const res = await api.subMerchant_saveApply(params)
          console.log('[AdminInfo] saveApply 响应:', res)

          if (res?.data?.success) {
            const savedData = res.data.data
            console.log('[AdminInfo] 保存成功')

            if (savedData?.id && !this.applyId) {
              this.$emit('update:applyId', savedData.id)
              this.$emit('update:applyNo', savedData.applyNo)
              console.log('[AdminInfo] 更新父组件 applyId:', savedData.id)
            }

            // ★★★ 通知父组件更新数据，传入完整的 savedData ★★★
            this.$emit('saved', savedData)
            this.$message.success('保存成功')
          } else {
            const msg = res?.data?.message || '保存失败'
            console.error('[AdminInfo] 保存失败:', msg)
            this.$message.error(msg)
          }
        } catch (e) {
          console.error('[AdminInfo] 保存异常:', e)
          this.$message.error('保存失败')
        } finally {
          this.saving = false
          console.log('========== [AdminInfo] submitModule 结束 ==========')
        }
      })
    },

    // ================================================================
    // ========== 错误信息提取 ==========
    // ================================================================

    extractMissingFields(msg) {
      if (!msg) return []
      const fieldMap = {
        '经办人证件类型': '证件类型',
        '经办人证件正面': '证件正面',
        '经办人证件反面': '证件反面',
        '经办人证件有效期开始': '证件有效期开始',
        '经办人证件有效期结束': '证件有效期结束',
        '授权函': '授权函',
        '管理员姓名': '管理员姓名',
        '管理员手机': '管理员手机号',
        '管理员邮箱': '管理员邮箱',
        '联系手机': '联系手机',
        '联系邮箱': '联系邮箱'
      }
      const missing = []
      for (const [key, label] of Object.entries(fieldMap)) {
        if (msg.includes(key)) {
          missing.push(label)
        }
      }
      return missing
    }
  }
}
</script>

<style scoped>
.upload-row { display: flex; gap: 8px; flex-wrap: wrap; }
.upload-row .el-input { flex: 1; min-width: 120px; }
.upload-row .el-button { flex-shrink: 0; }
.image-preview { margin-top: 8px; }
.thumb { width: 100px; height: 100px; border: 1px solid #eee; border-radius: 4px; object-fit: cover; }
.media-id-info { margin-top: 8px; font-size: 12px; color: #606266; display: flex; align-items: center; flex-wrap: wrap; gap: 4px; }
.media-id-label { font-weight: 500; color: #909399; }
.media-id-value { margin-left: 4px; color: #67c23a; word-break: break-all; max-width: 400px; }
.form-tip { font-size: 12px; color: #909399; margin-top: 4px; }
.module-footer { margin-top: 20px; padding-top: 16px; border-top: 1px solid #ebeef5; text-align: right; }
</style>